import { isEmpty } from "ramda";
import { useMemo, useRef, useState } from "react";
import { Button, DropdownSelect, Loading, Modal, Spinner, TextInput, Typography } from "@/components/atoms";
import { useOperator } from "@/hooks";
import { useGetCountryStatesQuery } from "@/redux/apis/resource/resourceApi";
import { useCreateVehicleMutation, useReactivateVehicleMutation, vehicleApi } from "@/redux/apis/vehicle/vehicleApi";
import { useAppDispatch } from "@/redux/hooks";
import { Vehicle } from "@/redux/slices/vehicle/types";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { addToast } from "@/utils";
import { VehicleVerificationErrorAlert } from "../common/VehicleVerificationErrorAlert";
import { getVerificationErrorType } from "../helpers";
import { ConfirmVehicleModal } from "./ConfirmVehicleModal";
import { VehicleInArchiveModal } from "./VehicleInArchiveModal";

interface AddVehicleModalProps {
  open: boolean;
  onClose: () => void;
}

export const AddVehicleModal = ({ open, onClose }: AddVehicleModalProps) => {
  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const { country } = useOperator();
  const { isFetching, data: response } = useGetCountryStatesQuery(country);
  const [verifyVehicle, { isLoading, error, reset }] = useCreateVehicleMutation();
  const [reactivateVehicle, { isLoading: isReactivating }] = useReactivateVehicleMutation();
  const [numberPlate, setNumberPlate] = useState<string>("");
  const [state, setState] = useState<string>("");
  const [pendingVehicle, setPendingVehicle] = useState<Vehicle | undefined>(undefined);

  const options = useMemo(
    () => response?.data.states.map((state) => ({ name: state.name, value: state.id })) ?? [],
    [response?.data.states]
  );

  const errorType = useMemo(() => {
    if (!error) return undefined;
    return getVerificationErrorType(error);
  }, [error]);

  const isArchivedError = Boolean(errorType && errorType.includes("vehicle-archived"));
  const archivedVehicleUuid = isArchivedError ? errorType!.replace("vehicle-archived-", "") : undefined;

  const handleClose = () => {
    setNumberPlate("");
    setState("");
    setPendingVehicle(undefined);
    reset();
    onClose();
  };

  const handleReactivate = () => {
    if (!archivedVehicleUuid) return;

    reactivateVehicle(archivedVehicleUuid)
      .unwrap()
      .then(() => {
        addToast("info", `Vehicle has been successfully reactivated`);
        dispatch(vehicleApi.util.invalidateTags(["Vehicle"]));
        handleClose();
      })
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!numberPlate || !state) return;

    verifyVehicle({
      dry_run: true,
      registration_number: numberPlate,
      state_uuid: state,
    })
      .unwrap()
      .then((res) => {
        setPendingVehicle(res);
      })
      .catch(() => {
        console.warn("Failed to verify vehicle");
      });
  };

  return (
    <>
      <Modal open={open && !pendingVehicle && !isArchivedError} onClose={handleClose} className="w-[85vw] !p-6 md:w-[400px] lg:w-[400px]">
        <div className="flex min-h-[500px] flex-col">
          <Typography variant="title">Add Vehicle</Typography>
          <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
            Enter the vehicle details below
          </Typography>

          <div className="relative mt-5 flex flex-1 flex-col">
            {isLoading && <Loading />}

            <form className="flex flex-1 flex-col" onSubmit={handleSubmit}>
              <label htmlFor="modal-number-plate" className="flex flex-col gap-1">
                <Typography variant="paragraph">Vehicle Plate Number</Typography>
                <TextInput
                  title="Only Alphabets and Numbers are allowed"
                  pattern="[a-zA-Z0-9\-]+"
                  placeholder="Enter Vehicle Plate Number"
                  id="modal-number-plate"
                  value={numberPlate}
                  onChange={(e) => setNumberPlate(e.target.value)}
                  hasError={errorType === "verification-failed" || errorType === "no-match"}
                  hasWarning={errorType === "vehicle-exists" || errorType === "vehicle-exists-duplicate"}
                  required
                  autoFocus
                  ref={inputRef}
                  maxLength={32}
                />
              </label>
              <label htmlFor="modal-state" className="mt-5 flex flex-col gap-1">
                <Typography variant="paragraph">Select the State of Your Vehicle</Typography>
                <DropdownSelect
                  id="modal-state"
                  className="w-full"
                  placeholder="Select State"
                  options={options}
                  value={state}
                  onChange={setState}
                  disabled={isEmpty(options) || isFetching}
                />
              </label>

              {errorType && <VehicleVerificationErrorAlert errorType={errorType} />}

              <div className="mt-auto flex flex-col gap-3">
                <Button type="submit" variant="primary" size="lg" className="w-full" disabled={isLoading || !numberPlate || !state}>
                  {isLoading && <Spinner />}
                  Find Vehicle
                </Button>
                <Button type="button" variant="secondary" size="lg" className="w-full" onClick={handleClose}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      </Modal>
      <ConfirmVehicleModal
        vehicle={pendingVehicle}
        onCancel={() => {
          setPendingVehicle(undefined);
          reset();
        }}
        onClose={handleClose}
      />
      <VehicleInArchiveModal
        open={Boolean(errorType && errorType.includes("vehicle-archived"))}
        onClose={() => reset()}
        onReactivate={handleReactivate}
        isReactivating={isReactivating}
      />
    </>
  );
};
